import axios from "axios";
import moment from "moment";
import { takeLatest, call, put, select } from "redux-saga/effects";
import { createAction } from "redux-actions";
import { queryParams } from "../../../../utils/queryParams";
import { API_DATE_FORMAT } from "../../../../app_constants";

export const SET_LOADING = "saga/activities/SET_LOADING";
export const SET_ALL_SITES_MODE = "saga/activities/SET_ALL_SITES_MODE";
export const SET_CHART_LOADING = "saga/activities/SET_CHART_LOADING";
export const SET_UPDATES_LOADING = "saga/activities/SET_UPDATES_LOADING";

export const SET_DATES = "saga/activities/SET_DATES";
export const UPDATE_DATES = "saga/activities/UPDATE_DATES";
export const SET_CUSTOM_DATES = "saga/activities/SET_CUSTOM_DATES";
export const UPDATE_CUSTOM_RANGE = "saga/activities/UPDATE_CUSTOM_RANGE";
export const FETCH_CURRENT_USER = "saga/activities/FETCH_CURRENT_USER";
export const FETCH_CURRENT_USER_SUCCESS = "saga/activities/FETCH_CURRENT_USER_SUCCESS";
export const SET_FILTERS_DIRTY = "saga/activities/SET_FILTERS_DIRTY";
export const FETCH_FILTER_SUCCESS = "saga/activities/FETCH_FILTER_DATA_SUCCESS";
export const UPDATE_FILTERS = "saga/activities/UPDATE_FILTERS";
export const RESET_FILTERS = "saga/activities/RESET_FILTERS";
export const RESET_FILTERS_SELECTED = "saga/activities/RESET_FILTERS_SELECTED";

export const FETCH_CHART_SUCCESS = "saga/activities/FETCH_CHART_SUCCESS";
export const UPDATE_TABLE_VIEW = "saga/activities/UPDATE_TABLE_VIEW";
export const UPDATE_TABLE_VIEW_SELECTED = "saga/activities/UPDATE_TABLE_VIEW_SELECTED";
export const UPDATE_CHARTS = "saga/activities/UPDATE_CHARTS";

const actionCreators = {
  fetchCurrentUser: createAction(FETCH_CURRENT_USER),
  updateDates: createAction(UPDATE_DATES),
  updateCustomRange: createAction(UPDATE_CUSTOM_RANGE),
  updateFilters: createAction(UPDATE_FILTERS),
  resetFilters: createAction(RESET_FILTERS),
  updateData: createAction(UPDATE_CHARTS),
  switchView: createAction(UPDATE_TABLE_VIEW)
};

export const {
  fetchCurrentUser,
  updateDates,
  updateCustomRange,
  updateFilters,
  updateData,
  resetFilters,
  switchView
} = actionCreators;

export default function* watchActivities() {
  yield takeLatest(FETCH_CURRENT_USER, getCurrentUserData);
  yield takeLatest(UPDATE_CHARTS, updateCharts);
  yield takeLatest(UPDATE_TABLE_VIEW, updateTableView);
  yield takeLatest(RESET_FILTERS, reset);
}

function* getCurrentUserData({ payload }) {
  yield put({ type: FETCH_CURRENT_USER_SUCCESS, payload: payload });

  if (!payload.availableAllSitesView) {
    yield put({ type: SET_ALL_SITES_MODE, payload: true });
    yield put({ type: SET_LOADING, payload: false });
  } else {
    yield setDates();
    try {
      yield fetchFilters();
      yield fetchChart("lineChart");
      yield fetchChart("table");
      yield put({ type: SET_LOADING, payload: false });
    } catch (error) {
      console.error(error);
    }
  }
}

function* fetchFilters() {
  const [organization_id, short_url, start_date, end_date] = yield getCommonApiParams();

  const params = {
    organization_id,
    short_url,
    start_date,
    end_date
  };

  const {
    data: { filters }
  } = yield call(
    axios.get,
    queryParams.apiVersion + "pages/staff_activity/activities_contacts_chart/filters",
    { params }
  );

  yield put({
    type: FETCH_FILTER_SUCCESS,
    payload: filters
  });
}

const sections = {
  lineChart: { key: "lineChartData", urn: "get_contact_log_values" },
  table: { key: "tableData", urn: "get_activities_contact_log_table" }
};

function* getCommonApiParams() {
  return yield select((state) => {
    const { organization_id, short_url } = state.commonReducer.commonProps;

    const {
      selectedFilters: { staffs = [], table_view },
      selectedRequestDates: { startDate, endDate }
    } = state.activities;

    const staff_ids = staffs.map((val) => val.id).join(",");

    return [organization_id, short_url, startDate, endDate, staff_ids, table_view];
  });
}

function* fetchChart(section) {
  const [organization_id, short_url, start_date, end_date, staff_ids, table_view] =
    yield getCommonApiParams();

  const params = {
    organization_id,
    short_url,
    start_date,
    end_date,
    staff_ids,
    ...(section === "lineChart" ? { table_view } : {})
  };

  const sagaArguments = {
    ...sections[section],
    params: { ...params }
  };

  yield getChart(sagaArguments);
}

function* getChart(sagaArguments) {
  const { key, urn, params } = sagaArguments;
  const url = `${queryParams.apiVersion}pages/staff_activity/activities_contacts_chart/${urn}`;
  try {
    yield put({
      type: FETCH_CHART_SUCCESS,
      payload: { [key]: { isLoading: true, content: {} } }
    });

    const { data } = yield call(axios.post, url, { ...params });
    yield put({
      type: FETCH_CHART_SUCCESS,
      payload: { [key]: { isLoading: false, content: data } }
    });
  } catch (err) {
    console.error(err);
  }
}

function* updateCharts() {
  yield put({ type: SET_FILTERS_DIRTY, payload: false });
  const chartDirty = yield select((state) => state.activities.chartDirty);
  try {
    chartDirty ? yield fetchChart("lineChart") : "";

    yield fetchChart("table");
  } catch (error) {
    console.error(error);
  }
}

function* updateTableView({ payload }) {
  yield put({ type: UPDATE_TABLE_VIEW_SELECTED, payload });
  try {
    yield fetchChart("lineChart");
  } catch (error) {
    console.error(error);
  }
}

function* getAllData() {
  yield fetchChart("lineChart");
  yield fetchChart("table");
}

function* reset() {
  yield put({ type: SET_UPDATES_LOADING, payload: true });
  yield setDates();

  yield put({
    type: RESET_FILTERS_SELECTED
  });

  try {
    yield getAllData();
    yield put({ type: SET_UPDATES_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* setDates() {
  const { calendarEndDate: endDate, calendarStartDate } = yield select(
    (state) => state.activities.currentUser
  );
  const startDate = moment(endDate).subtract(11, "month").format(API_DATE_FORMAT);

  yield put({
    type: SET_DATES,
    payload: {
      startDate,
      endDate,
      calendarStartDate
    }
  });
}
